import { useState } from 'react';
import { useTranslation } from 'next-i18next';

import { type AchievementStandard } from '@/shared/features/ai-quiz-generation/types';
import { alertDialog } from '@/ui_v3';

export const MAX_SELECTION = 5;

type AchievementStandardWithOrigin = AchievementStandard & {
  origin?: 'recommend' | 'search';
};

/**
 * 성취기준 선택 상태를 관리하는 훅
 * 최대 5개까지 선택 가능하며, 초과 시 경고 다이얼로그를 표시합니다.
 */
export const useStandardSelection = ({
  alreadySelectedAchievementStandards,
}: {
  alreadySelectedAchievementStandards: AchievementStandardWithOrigin[];
}) => {
  const { t } = useTranslation(['build']);
  const [selectedAchievementStandards, setSelectedAchievementStandards] =
    useState<AchievementStandardWithOrigin[]>(
      alreadySelectedAchievementStandards,
    );

  /**
   * 성취기준 선택/해제를 토글합니다.
   * 최대 선택 개수를 초과하면 경고 다이얼로그를 표시합니다.
   */
  const toggleAchievementStandard = (
    achievementStandard: AchievementStandardWithOrigin,
  ) => {
    setSelectedAchievementStandards(prev => {
      const isSelected = prev.find(s => s.code === achievementStandard.code);
      if (isSelected) {
        return prev.filter(s => s.code !== achievementStandard.code);
      } else if (prev.length < MAX_SELECTION) {
        return [...prev, achievementStandard];
      } else {
        alertDialog.open({
          header: {
            title: t('build:build.standard.modal.maxSelection', {
              max: MAX_SELECTION,
            }),
          },
        });
        return prev;
      }
    });
  };

  /**
   * 전체 선택 (최대 개수까지만)
   */
  const selectAll = (achievementStandards: AchievementStandard[]) => {
    const limited = achievementStandards.slice(0, MAX_SELECTION);
    setSelectedAchievementStandards(limited);
  };

  /**
   * 전체 해제
   */
  const clearAll = () => {
    setSelectedAchievementStandards([]);
  };

  return {
    selectedAchievementStandards,
    toggleAchievementStandard,
    selectAll,
    clearAll,
    isMaxReached: selectedAchievementStandards.length >= MAX_SELECTION,
  };
};
